'use client'

import classnames from 'classnames'
import styles from './Locations3UpOpenRolesCard.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import useI18n from '@/hooks/use-i18n'
import { getRestaurantAddressString } from '@/utils/format'
import Widont from '@/components/Widont/Widont'
import AnimateContainer from '@/components/AnimateContainer/AnimateContainer'
import Button from '@/components/Button/Button'

type Locations3UpOpenRolesCardProps = SanityLocations3UpOpenRolesItem & {
  className?: string
}

const Locations3UpOpenRolesCard = ({
  className,
  location,
  openingsCount,
  ctaLink,
}: Locations3UpOpenRolesCardProps) => {
  const { i18n, isValidI18nKey } = useI18n()
  const { locationData, title } = location || {}

  const addressString = getRestaurantAddressString({
    streetAddress: locationData?.streetAddress,
    city: locationData?.city,
    state: (isValidI18nKey(locationData?.state) && i18n(locationData?.state)) || '',
    postalCode: locationData?.postalCode,
  })

  if (!locationData || !locationData?.featuredImage || !title) return null

  return (
    <AnimateContainer
      element="article"
      animationType="fadeIn"
      className={classnames(styles.Locations3UpOpenRolesCard, className)}
    >
      <div className={styles.imageContainer}>
        <SanityImage
          className={styles.image}
          image={locationData?.featuredImage}
          breakpoints={{
            tablet: {
              image: locationData?.featuredImage,
              width: 900,
              options: {
                height: 1000,
                fit: 'crop',
              },
            },
            xs: {
              image: locationData?.featuredImage,
              width: 800,
              options: {
                height: 950,
                fit: 'crop',
              },
            },
          }}
        />
      </div>
      <div className={styles.textContainer}>
        <p className={styles.title}>
          <Widont text={title} />
        </p>
        {openingsCount > 0 && (
          <div className={styles.openingsBlurb}>
            <span className={styles.dot} />
            <span className={styles.openingsText}>
              {openingsCount} {openingsCount === 1 ? 'opening' : 'openings'}
            </span>
          </div>
        )}
        {locationData?.phoneNumber && (
          <p className={styles.phoneNumber}>
            {i18n('phoneNumberShort', { phoneNumber: locationData?.phoneNumber })}
          </p>
        )}
        {addressString && (
          <p className={styles.address}>
            {i18n('addressShort', { address: addressString })}
          </p>
        )}
        {ctaLink && (
          <Button
            className={styles.ctaButton}
            link={ctaLink}
            icon="caretRight"
          />
        )}
      </div>
    </AnimateContainer>
  )
}

Locations3UpOpenRolesCard.displayName = 'Locations3UpOpenRolesCard'

export default Locations3UpOpenRolesCard
